import React, { useState, useCallback, useMemo } from 'react';
import { Drawer as MUIDrawer, List, ListItemButton, Box, ListItemIcon, ListItemText, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { HomeOutlined, GroupOutlined, AppsOutlined, DvrOutlined, SettingsOutlined, MenuOutlined, CloseOutlined } from '@mui/icons-material';
import { INTERNAL_APPS } from '../helper/constants';
import useAuth from '../hooks/useAuth';

const drawerWidth = 256;
const collapsedWidth = 64;

const Drawer = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();
  const { filteredApps } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isActivePath = useCallback(
    (path) => {
      if (!loc?.pathname) return false;
      if (path === '/') return loc.pathname === '/';
      return loc.pathname === path || loc.pathname.startsWith(`${path}/`);
    },
    [loc?.pathname]
  );

  const handleMouseEnter = useCallback(() => {
    if (!isMobile) {
      setIsHovered(true);
    }
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (!isMobile) {
      setIsHovered(false);
    }
  }, [isMobile]);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(!mobileOpen);
  }, [mobileOpen]);

  const handleLinkClick = useCallback(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [isMobile]);

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 2, pb: 1 }}>
          <IconButton
            onClick={handleDrawerToggle}
            size="small"
            sx={{
              backgroundColor: '#f8fafc',
              '&:hover': { backgroundColor: '#e5e7eb' }
            }}
          >
            <CloseOutlined fontSize="small" />
          </IconButton>
        </Box>
      )}
      <List sx={{ flex: 1, pt: isMobile ? 1 : 2 }}>
        {filteredApps.map((item) => {
          const isActive = isActivePath(item.path);
          return (
            <ListItemButton
              key={item.text}
              sx={{
                ...styles.listItemButton,
                color: isActive ? 'var(--primary-color)' : 'var(--text-primary)',
                backgroundColor: isActive ? '#ecfdf3' : 'transparent',
                minHeight: 48,
              }}
              selected={isActive}
              component={Link}
              to={item.path}
              onClick={handleLinkClick}
            >
              <ListItemIcon
                sx={{
                  color: isActive ? 'var(--primary-color)' : 'var(--text-secondary)',
                  minWidth: isMobile ? 56 : 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              {(isHovered || isMobile) && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 700 : 500,
                    fontSize: isMobile ? '1rem' : '0.875rem',
                  }}
                />
              )}
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  return (
    <>
      {isMobile && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={styles.menuButton}
        >
          <MenuOutlined />
        </IconButton>
      )}

      {/* Mobile drawer */}
      {isMobile ? (
        <MUIDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              maxWidth: '80vw',
              boxSizing: 'border-box',
              backgroundColor: '#ffffff',
              borderRight: '1px solid var(--border-subtle)',
              boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
            },
          }}
        >
          {drawerContent}
        </MUIDrawer>
      ) : (
        /* Desktop drawer */
        <MUIDrawer
          variant="permanent"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            ...styles.drawer,
            width: isHovered ? drawerWidth : collapsedWidth,
            [`& .MuiDrawer-paper`]: {
              ...styles.drawerPaper,
              width: isHovered ? drawerWidth : collapsedWidth,
            },
          }}
        >
          {drawerContent}
        </MUIDrawer>
      )}
    </>
  );
};

const styles = {
  drawer: {
    flexShrink: 0,
    overflow: 'hidden',
    display: { xs: 'none', sm: 'block' },
  },
  drawerPaper: {
    boxSizing: 'border-box',
    top: { xs: '56px', sm: '64px' },
    height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
    transition: 'width 0.3s',
    overflowX: 'hidden',
    backgroundColor: '#ffffff',
    borderRight: '1px solid var(--border-subtle)',
    boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
  },
  listItemButton: {
    py: 1.5,
    px: 2,
    mx: 1,
    my: 0.5,
    borderRadius: 2,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f0fdf4',
      color: 'var(--primary-color)',
    },
    '&.Mui-selected': {
      backgroundColor: '#ecfdf3',
      '&:hover': {
        backgroundColor: '#dcfce7',
      },
    },
  },
  menuButton: {
    position: 'fixed',
    left: 12,
    top: 10,
    zIndex: 1200,
    backgroundColor: 'transparent',
    color: 'var(--text-primary)',
    padding: 1,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
};

const menuItems = [
  { text: 'Dashboard', path: '/', icon: <HomeOutlined /> },
  { text: 'User Management', path: '/user-management', icon: <GroupOutlined /> },
  { text: 'Role Management', path: '/role-management', icon: <SettingsOutlined /> },
  { text: 'Application Management', path: '/applications', icon: <AppsOutlined /> },
  { text: 'Activity Log', path: '/activity-log', icon: <DvrOutlined /> },
];

export default Drawer;
